import { useQueries, useQuery } from '@tanstack/react-query'

import type {
  GetBusinessUnitResponse,
  GetProductResponse,
  ListBusinessUnitsResponse,
  ListProductsResponse,
} from '@/features/ecosystem/types'

import { api } from '@/lib/api-client'

export interface EcosystemSearchItem {
  kind: 'unit' | 'product'
  label: string
  sublabel: string
  to: string
}

export function useBusinessUnits() {
  return useQuery({
    queryKey: ['ecosystem', 'business-units'],
    queryFn: async () => {
      const { data } = await api.get<ListBusinessUnitsResponse>(
        '/api/ecosystem/business-units',
        { params: { perPage: 100 } },
      )
      return data.businessUnits
    },
  })
}

export function useBusinessUnit(slug: string) {
  return useQuery({
    queryKey: ['ecosystem', 'business-units', slug],
    queryFn: async () => {
      const { data } = await api.get<GetBusinessUnitResponse>(
        `/api/ecosystem/business-units/${slug}`,
      )
      return data.businessUnit
    },
    enabled: slug.length > 0,
  })
}

export function useProducts(buSlug: string) {
  return useQuery({
    queryKey: ['ecosystem', 'products', buSlug],
    queryFn: async () => {
      const { data } = await api.get<ListProductsResponse>(
        `/api/ecosystem/business-units/${buSlug}/products`,
        { params: { perPage: 100 } },
      )
      return data.products
    },
    enabled: buSlug.length > 0,
  })
}

export function useProduct(buSlug: string, productSlug: string) {
  return useQuery({
    queryKey: ['ecosystem', 'product', buSlug, productSlug],
    queryFn: async () => {
      const { data } = await api.get<GetProductResponse>(
        `/api/ecosystem/business-units/${buSlug}/products/${productSlug}`,
      )
      return data.product
    },
    enabled: buSlug.length > 0 && productSlug.length > 0,
  })
}

export function useEcosystemSearch(): { items: EcosystemSearchItem[]; isLoading: boolean } {
  const { data: units, isLoading: unitsLoading } = useBusinessUnits()
  const unitList = units ?? []

  const productQueries = useQueries({
    queries: unitList.map((unit) => ({
      queryKey: ['ecosystem', 'products', unit.slug],
      queryFn: async () => {
        const { data } = await api.get<ListProductsResponse>(
          `/api/ecosystem/business-units/${unit.slug}/products`,
          { params: { perPage: 100 } },
        )
        return data.products
      },
    })),
  })

  const items: EcosystemSearchItem[] = []
  unitList.forEach((unit, index) => {
    items.push({
      kind: 'unit',
      label: unit.name,
      sublabel: 'Unidade de negócio',
      to: `/ecosystem/${unit.slug}`,
    })
    for (const product of productQueries[index]?.data ?? []) {
      items.push({
        kind: 'product',
        label: product.name,
        sublabel: unit.name,
        to: `/ecosystem/${unit.slug}/${product.slug}`,
      })
    }
  })

  return {
    items,
    isLoading: unitsLoading || productQueries.some((query) => query.isLoading),
  }
}
